import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ColumnEvent,
  ColumnState,
  GridApi,
  GridReadyEvent,
} from 'ag-grid-community'
import { styled } from '@mui/material'
import { BulkSheetView } from '../../../../containers/BulkSheetView'
import { getGanttRowItem, useMasterScheduleGridOptions } from './gridOptions'
import { ComputedMasterScheduleRowData } from './model'
import {
  VersionReportStatus,
  VersionReportVersionPageRowEntity,
} from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { generateGanttScale } from '../../../../containers/commons/AgGrid/components/cell/custom/gantt/ganttUtil'
import { ProgressLineOverlay } from '../../../../containers/commons/AgGrid/components/cell/custom/gantt/ProgressLineOverlay'
import {
  getGanttChartWidth,
  GANTT_FIT_MIN_CONTENT_RATIO,
} from '../../../ProjectPlan/projectPlanOptions'
import {
  GanttDisplayUnit,
  GanttParameterVO,
} from '../../../../../domain/value-object/GanttParameterVO'
import { CalendarDateVO } from '../../../../../domain/value-object/CalendarDateVO'
import { DayOfWeek } from '../../../../../lib/functions/report'
import DateVO from '../../../../../vo/DateVO'
import { useProjectPrivateContext } from '../../../../context/projectContext'
import {
  AggregateFieldToggleGroup,
  AggregateTargetToggleGroup,
} from '../../../../components/toggleGroups'
import {
  StoredVersionReportPageDisplayState,
  toStoredColumnState,
  columnEventValidSources,
} from '../../hooks/pageDisplayState'
import {
  computeProgressValues,
  DEFAULT_AGGREGATE_FIELD,
  DEFAULT_AGGREGATE_TARGET,
  useMasterScheduleData,
} from './hooks/data'
import { useSelector } from 'react-redux'
import { AllState } from '../../../../../store'
import { getFinancialStartMonth } from '../../../../../utils/date'
import { useGridContext } from '../../../../containers/BulkSheetView/hooks/useGridContext'
import { GanttChartParameter } from '../../../../containers/BulkSheetView/components/header/GanttChartParameter'
import DisplayFormatButton from '../../../../containers/BulkSheetView/components/header/DisplayFormatButton'
import ColumnSettingButton from '../../../../containers/BulkSheetView/components/header/ColumnSettingButton'
import { useColumnSetting } from '../../../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import { ColumnConfigPopper } from '../dialog/ColumnConfigPopper'
import { PendingEdits } from '../../hooks/currentReport'

const DEFAULT_ROW_HEIGHT = 45

type MasterScheduleProps = {
  editable: boolean
  projectUuid: string
  rootUuid: string | undefined
  targetUuids: string[]
  snapshotUuid: string
  pageRows: VersionReportVersionPageRowEntity[]
  updateRow: (
    rowUuid: string,
    lockVersion: number,
    key: 'status' | 'comment',
    value: string
  ) => Promise<boolean>
  baseDate?: Date
  displayState: StoredVersionReportPageDisplayState
  updateDisplayState: (
    updated: Partial<StoredVersionReportPageDisplayState>
  ) => void
  displayStateRestoreToken?: number
  pendingEditsRef: MutableRefObject<PendingEdits>
  discardToken: number
  callbackWhenStartLoading?: () => void
  callbackWhenFinishLoading?: () => void
}
export const MasterSchedule = ({
  editable,
  projectUuid,
  rootUuid,
  snapshotUuid,
  targetUuids,
  pageRows,
  updateRow,
  baseDate,
  displayState,
  updateDisplayState,
  displayStateRestoreToken,
  pendingEditsRef,
  discardToken,
  callbackWhenStartLoading,
  callbackWhenFinishLoading,
}: MasterScheduleProps) => {
  const fiscalStartMonth = useSelector((state: AllState) =>
    getFinancialStartMonth(state.tenant.organization?.financialYearEndMonth)
  )
  const { project, wbsItemTypes } = useProjectPrivateContext()
  const gridApiRef = useRef<GridApi>()
  const ref = useRef<HTMLDivElement>(null)
  const [isGridReady, setIsGridReady] = useState(false)
  const handleStatusChange = useCallback(
    (
      data: ComputedMasterScheduleRowData,
      status: VersionReportStatus,
      _rowIndex: number
    ) => {
      const row = pageRows.find(r => r.targetUuid === data.uuid)
      if (!row) return
      const currentStatus =
        pendingEditsRef.current.rows.get(row.uuid)?.status ?? row.status
      if (currentStatus === status) return
      void updateRow(row.uuid, row.lockVersion, 'status', status)
    },
    [pageRows, updateRow, pendingEditsRef]
  )
  const gridOptions = useMasterScheduleGridOptions({
    editable,
    onChangeStatus: handleStatusChange,
  })
  const { updateContext } = useGridContext(
    gridApiRef.current,
    gridOptions.context
  )
  const { rows } = useMasterScheduleData(
    projectUuid,
    snapshotUuid,
    targetUuids,
    callbackWhenStartLoading,
    callbackWhenFinishLoading
  )
  const aggregateTarget =
    displayState.aggregateTarget ?? DEFAULT_AGGREGATE_TARGET
  const aggregateField = displayState.aggregateField ?? DEFAULT_AGGREGATE_FIELD
  const statusByTargetUuid = useMemo(
    () =>
      new Map(
        pageRows.map(r => [
          r.targetUuid,
          pendingEditsRef.current.rows.get(r.uuid)?.status ?? r.status,
        ])
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pendingEditsRef は ref なので依存不要
    [pageRows, discardToken]
  )
  const computedRows = useMemo(
    () =>
      rows.map(row => ({
        ...row,
        status: statusByTargetUuid.get(row.uuid),
        progressValues: computeProgressValues(
          row.progressValue,
          aggregateTarget,
          aggregateField
        ),
      })),
    [rows, statusByTargetUuid, aggregateTarget, aggregateField]
  )
  const [ganttTimeScale, setGanttTimeScale] = useState<CalendarDateVO[]>([])
  const ganttParameter = useMemo(() => {
    if (displayState.ganttParameter) {
      return GanttParameterVO.deserialize(displayState.ganttParameter)
    }
    const start = new DateVO(project.scheduledDate.startDate)
    const end = new DateVO(project.scheduledDate.endDate)
    const months = end.diffMonth(start)
    const unit =
      months >= 24
        ? GanttDisplayUnit.QUARTER
        : months >= 6
          ? GanttDisplayUnit.MONTH
          : months >= 1
            ? GanttDisplayUnit.WEEK
            : GanttDisplayUnit.DAY
    return new GanttParameterVO(
      unit,
      DayOfWeek.MONDAY,
      { start, end },
      GanttParameterVO.defaultDisplayOptions()
    )
  }, [
    displayState.ganttParameter,
    project.scheduledDate.startDate,
    project.scheduledDate.endDate,
  ])

  const showProgressLine =
    ganttParameter.displayOptions?.showProgressLine ?? false

  const [isGanttVisible, setIsGanttVisible] = useState(true)
  const onSwitchingGanttChart = useCallback((visible: boolean) => {
    setIsGanttVisible(visible)
    gridApiRef.current?.setColumnsVisible(['ganttChart'], visible)
  }, [])

  const updateColumnStateConfig = useCallback(
    (state: ColumnState[]) => {
      // ガント列の幅・表示は動的制御が優先のため、並び順維持に必要な colId のみ保存する
      const stored = state.map(v =>
        v.colId === 'ganttChart' ? { colId: v.colId } : toStoredColumnState(v)
      )
      updateDisplayState({ columnState: stored })
    },
    [updateDisplayState]
  )

  const handleOptimizeGanttColumnWidth = useCallback(() => {
    const api = gridApiRef.current
    const viewportEl = ref.current?.querySelector<HTMLElement>(
      '.ag-center-cols-viewport'
    )
    if (!api || !viewportEl) return

    const otherColumnsWidth = api
      .getDisplayedCenterColumns()
      .filter(c => c.getColId() !== 'ganttChart')
      .reduce((sum, c) => sum + c.getActualWidth(), 0)

    const availableGanttWidth = viewportEl.clientWidth - otherColumnsWidth
    if (availableGanttWidth <= 0) return
    const contentWidth = getGanttChartWidth(ganttParameter)
    const minWidth =
      contentWidth * GANTT_FIT_MIN_CONTENT_RATIO[ganttParameter.unit]
    const newWidth = Math.max(availableGanttWidth, minWidth)
    api.setColumnWidths([{ key: 'ganttChart', newWidth }])
    updateColumnStateConfig(api.getColumnState())
  }, [ganttParameter, updateColumnStateConfig])

  const onColumnChanged = useCallback(
    (e: ColumnEvent) => {
      if (!e.api) return
      if (!e.source || !columnEventValidSources.has(e.source)) return
      if ('finished' in e && e.finished === false) return
      updateColumnStateConfig(e.api.getColumnState())
    },
    [updateColumnStateConfig]
  )

  const columnSettingDialog = useColumnSetting()

  const applyStoredColumnState = useCallback(() => {
    const { columnState } = displayState
    if (gridApiRef.current && columnState && columnState.length > 0) {
      gridApiRef.current.applyColumnState({
        state: columnState,
        applyOrder: true,
      })
    }
  }, [displayState])

  const applyStoredColumnStateRef = useRef(applyStoredColumnState)
  useEffect(() => {
    applyStoredColumnStateRef.current = applyStoredColumnState
  }, [applyStoredColumnState])

  // Redis から復元した時 (token 変化時) のみ列状態を再適用する
  useEffect(() => {
    applyStoredColumnStateRef.current()
  }, [displayStateRestoreToken])

  useEffect(() => {
    gridApiRef.current?.resetRowHeights()
  }, [displayState.rowHeight])

  const onGridReady = useCallback((event: GridReadyEvent) => {
    gridApiRef.current = event.api
    applyStoredColumnStateRef.current()
    setIsGridReady(true)
  }, [])

  const initGanttParam = useCallback(async () => {
    const [ganttDisplayTimeScale, ganttTimeScale] =
      await generateGanttScale(ganttParameter)
    setGanttTimeScale(ganttTimeScale)
    updateContext({
      ganttParameter,
      ganttDisplayTimeScale,
      ganttTimeScale,
      progressLineEnabled: showProgressLine,
      ganttBaseDate: baseDate,
    })
    gridApiRef.current?.refreshHeader()
    gridApiRef.current?.refreshCells({ columns: ['ganttChart'], force: true })
    gridApiRef.current?.setColumnWidths([
      { key: 'ganttChart', newWidth: getGanttChartWidth(ganttParameter) },
    ])
  }, [ganttParameter, showProgressLine, updateContext, baseDate])

  useEffect(() => {
    if (!isGridReady) return
    initGanttParam()
  }, [initGanttParam, isGridReady])
  return (
    <>
      <HeaderArea>
        <AggregateTargetToggleGroup
          value={aggregateTarget}
          onChange={v => updateDisplayState({ aggregateTarget: v })}
        />
        <AggregateFieldToggleGroup
          value={aggregateField}
          onChange={v => updateDisplayState({ aggregateField: v })}
        />
        {editable && (
          <>
            <ColumnSettingButton
              onClick={columnSettingDialog.toggle}
              open={columnSettingDialog.isOpen}
            />
            <DisplayFormatButton
              height={displayState.rowHeight ?? DEFAULT_ROW_HEIGHT}
              onChangeHeight={v => updateDisplayState({ rowHeight: v })}
            />
            <GanttChartParameter
              value={isGanttVisible}
              onSwitchingGanttChart={onSwitchingGanttChart}
              onOptimizeGanttColumnWidth={handleOptimizeGanttColumnWidth}
              parameter={ganttParameter}
              onChangeParameter={v =>
                updateDisplayState({ ganttParameter: v.serialize() })
              }
              fiscalStartMonth={fiscalStartMonth}
            />
          </>
        )}
      </HeaderArea>
      {computedRows.length > 0 && (
        <BulkSheetView
          ref={ref}
          gridOptions={gridOptions}
          rowData={computedRows}
          rowHeight={displayState.rowHeight ?? DEFAULT_ROW_HEIGHT}
          onColumnVisible={onColumnChanged}
          onColumnResized={onColumnChanged}
          onColumnMoved={onColumnChanged}
          onGridReady={onGridReady}
        />
      )}
      {editable && columnSettingDialog.isOpen && gridApiRef.current && (
        <ColumnConfigPopper
          anchorEl={columnSettingDialog.anchorEl as Element | undefined}
          onClose={columnSettingDialog.close}
          gridApi={gridApiRef.current}
          updateConfig={updateColumnStateConfig}
          height={ref.current?.offsetHeight}
        />
      )}
      {gridApiRef.current &&
        ganttTimeScale.length > 0 &&
        showProgressLine &&
        isGanttVisible && (
          <ProgressLineOverlay
            gridApi={gridApiRef.current}
            timeScale={ganttTimeScale}
            aggregateTarget={aggregateTarget}
            aggregateField={aggregateField}
            unit={ganttParameter.unit}
            getGanttRowItem={getGanttRowItem}
            baseDate={baseDate}
          />
        )}
    </>
  )
}

const HeaderArea = styled('div')({
  display: 'flex',
  alignItems: 'center',
  margin: '10px 10px 5px 10px',
  gap: '10px',
})
